import React, { useMemo, useState } from "react";

import * as Dialog from "@radix-ui/react-dialog";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Copy,
  Info,
  Loader2,
  Pencil,
  RotateCcw,
  Sparkles,
  X,
} from "lucide-react";

import { AttributeReviewRowChips } from "./AttributeReviewRowChips";
import { AttributeReviewSourceLabel } from "./AttributeReviewSourceLabel";
import { LeafDecisionBox } from "./LeafDecisionBox";
import { PoolSection, type Pool } from "./PoolSection";
import type { PoolValue } from "@/lib/attributeReview/valueView";
import type { AttributeReviewDraftSelection } from "@/hooks/useAttributeReview.types";
import { shownAnswer } from "@/lib/attributeReview/shownAnswer";
import { useRowActions, useRowDecision } from "./useRowDecision";
import { Button } from "@/components/ui/Button";
import { ConfirmationModal, showToast } from "@/components/ui";
import { rowRequiresManualDecisions } from "@/lib/attributeReview/rowRequiresManualDecisions";
import { canInlineApprove } from "@/lib/attributeReview/reviewSignals";
import { getAttributeReviewGridClass } from "@/lib/attributeReview/getAttributeReviewGridClass";
import {
  buildListLine,
  type ListItem,
} from "@/lib/attributeReview/buildListLine";
import { getApprovalPipelineStatusLabel } from "@/lib/attributeReview/approvalPipeline";
import { cn } from "@/lib/utils";
import type { AttributeReviewMappingRow } from "@/hooks/useAttributeReview";

import type { AttributeReviewRowProps } from "./AttributeReviewRow.types";

export const AttributeReviewRow: React.FC<AttributeReviewRowProps> = ({
  sessionId,
  mapping: mappingProp,
  isExpanded,
  onToggle,
  onStageApproval,
  onApproveRowImmediately,
  onSaveRow,
  onCommitBucket,
  onDiscardBucket,
  onRevertStaged,
  approvedBucketIds,
  approvedBucketSize,
  isCommittingBucket,
  approvalPipeline,
  isApproving = false,
  onDraftReviewChange,
  draftSelection,
  isSelected = false,
  onSelectChange,
  showCheckbox = false,
  showConfidence = true,
  isPreApproved = false,
  isRejected = false,
  onUnreject,
  onRejectField,
  onNavigatePrev,
  onNavigateNext,
  hasPrev = false,
  hasNext = false,
}) => {
  const mapping = mappingProp as Record<string, unknown>;
  const mappingId = String(mapping.id ?? "");
  const onDraftChange = (next: AttributeReviewDraftSelection | null) =>
    onDraftReviewChange?.(mappingId, next);
  const decision = useRowDecision(draftSelection, onDraftChange);
  const actions = useRowActions(
    String(sessionId ?? ""),
    mappingId,
    draftSelection,
    onDraftChange,
  );
  const shown = shownAnswer(mapping, draftSelection);
  const [isSavingRow, setIsSavingRow] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // True while onStageApproval is running. The footer Approve button shows a
  // spinner during this window; auto-advance only fires on success.
  const [isStaging, setIsStaging] = useState(false);
  // When the reviewer attempts to close the modal with a non-empty bucket,
  // we intercept the close and show a save / discard panel instead.
  const [showSaveDiscardPrompt, setShowSaveDiscardPrompt] = useState(false);
  const isRowStaged = approvedBucketIds.has(String(mapping.id));
  const listLine = buildListLine(mapping as ListItem);
  const requiresManualDecisions = useMemo(
    () => rowRequiresManualDecisions(mapping as AttributeReviewMappingRow),
    [mapping],
  );

  const gridClass = getAttributeReviewGridClass(showCheckbox, showConfidence);
  const llmReasoning =
    typeof mapping.llmReasoning === "string" && mapping.llmReasoning.trim()
      ? mapping.llmReasoning.trim()
      : typeof mapping.reason === "string" && mapping.reason.trim()
        ? mapping.reason.trim()
        : null;
  const sourceAttributeLabel = String(
    mapping.sourceAttribute ?? mapping.id ?? "",
  );
  const requirementLevel =
    typeof mapping.requirementLevel === "string"
      ? mapping.requirementLevel.toUpperCase()
      : null;
  // Superscript "*" warning the reviewer this attribute is marketplace-required
  // (rose) or conditionally required (amber) before they ignore it. The full
  // message is in both title and aria-label (role="img") so screen-reader users
  // get the same guidance sighted users get from the tooltip.
  const requiredWarning =
    "Required by the marketplace — do not ignore; publishing will fail without it.";
  const conditionalWarning =
    "Conditionally required — required only when related fields take certain values; check before ignoring.";
  const requiredStarNode =
    requirementLevel === "REQUIRED" ? (
      <span
        className="ml-0.5 align-super text-[0.9em] font-bold text-rose-500 dark:text-rose-400"
        role="img"
        title={requiredWarning}
        aria-label={requiredWarning}
      >
        *
      </span>
    ) : requirementLevel === "CONDITIONALLY_REQUIRED" ? (
      <span
        className="ml-0.5 align-super text-[0.9em] font-bold text-amber-500 dark:text-amber-400"
        role="img"
        title={conditionalWarning}
        aria-label={conditionalWarning}
      >
        *
      </span>
    ) : null;
  const marketplaceRawJson =
    mapping.marketplaceRawJson && typeof mapping.marketplaceRawJson === "object"
      ? (mapping.marketplaceRawJson as Record<string, unknown>)
      : {};
  const marketplaceRawJsonText = JSON.stringify(marketplaceRawJson, null, 2);
  const handleCopyMarketplaceJson = async () => {
    try {
      await navigator.clipboard.writeText(marketplaceRawJsonText);
      showToast.success("Marketplace JSON copied");
    } catch (error) {
      showToast.error(
        "Copy failed",
        error instanceof Error
          ? error.message
          : "Could not copy Marketplace JSON to the clipboard.",
      );
    }
  };
  const stageCurrentRow = async (): ReturnType<
    AttributeReviewRowProps["onStageApproval"]
  > => {
    return onStageApproval(mappingProp);
  };
  const handleApproveFooterClick = async () => {
    if (isStaging || isCommittingBucket) return;
    setIsStaging(true);
    let staged: Awaited<ReturnType<typeof stageCurrentRow>> = null;
    try {
      staged = await stageCurrentRow();
    } finally {
      setIsStaging(false);
    }
    if (!staged) return;
    // Auto-advance only on a successful stage. If there is no next pending
    // row, the bucket is committed in one bulk mutation right here — the
    // modal stays open and shows the locked saving overlay until the
    // mutation resolves (or returns to the row on validation failure).
    if (hasNext && onNavigateNext) {
      onNavigateNext();
      return;
    }
    await onCommitBucket([staged]);
  };
  // Save button. When the reviewer has staged approvals (clicked Approve on
  // earlier rows), Save commits the whole bucket — "Save these N". With nothing
  // staged it persists just THIS row immediately and advances to the next
  // unapproved item (or closes the popup if it was the last). Failures render
  // inline in the popup and the row stays open for a retry.
  const handleSaveClick = async () => {
    if (isStaging || isSavingRow || isCommittingBucket) return;
    setSaveError(null);
    if (approvedBucketSize > 0) {
      const ok = await onCommitBucket();
      if (!ok) {
        setSaveError(
          "Couldn't save the staged approvals. Review the highlighted rows and try again.",
        );
      }
      return;
    }
    if (!onSaveRow) return;
    setIsSavingRow(true);
    let result: { ok: boolean; error: string | null };
    try {
      result = await onSaveRow(mappingProp);
    } finally {
      setIsSavingRow(false);
    }
    if (!result.ok) {
      setSaveError(result.error ?? "Save failed. Please try again.");
      return;
    }
    if (hasNext && onNavigateNext) {
      onNavigateNext();
    } else {
      onToggle();
    }
  };
  const handleInlineApproveClick = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    if (isStaging || isCommittingBucket) return;
    if (!onApproveRowImmediately) return;
    setIsStaging(true);
    try {
      await onApproveRowImmediately(mappingProp);
    } finally {
      setIsStaging(false);
    }
  };

  const handleRevertClick = () => {
    if (isCommittingBucket) return;
    onRevertStaged(String(mapping.id));
  };
  const [rejectFieldConfirmOpen, setRejectFieldConfirmOpen] = useState(false);
  const [isRejectingField, setIsRejectingField] = useState(false);
  const handleRejectFieldConfirm = async () => {
    // The dialog can be opened just before the row's save queues — never
    // reject a row that is riding an in-flight approval job.
    if (isApproving) {
      setRejectFieldConfirmOpen(false);
      return;
    }
    if (!onRejectField || isRejectingField) return;
    setIsRejectingField(true);
    try {
      const ok = await onRejectField(mappingProp as AttributeReviewMappingRow);
      if (ok) setRejectFieldConfirmOpen(false);
    } finally {
      setIsRejectingField(false);
    }
  };

  return (
    <div
      id={`attribute-review-row-${mapping.id}`}
      data-testid={`review-row-${mapping.id}`}
      className={cn(
        "border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden mb-3",
        "border-l-4 border-l-transparent",
        "transition-all duration-200",
        mapping.aiBand === "LOW" && "!border-l-rose-500",
        mapping.aiBand === "MEDIUM" && "!border-l-amber-500",
        mapping.aiBand === "HIGH" && "!border-l-emerald-500",
      )}
    >
      <div
        className={cn(
          "grid gap-4 p-4 cursor-pointer min-w-[760px]",
          gridClass,
          "bg-white dark:bg-slate-900",
          "hover:bg-slate-50 dark:hover:bg-slate-800/50",
          "transition-colors duration-150",
          isExpanded && "bg-slate-50 dark:bg-slate-800/50",
          isSelected && "bg-indigo-50/50 dark:bg-indigo-950/30",
        )}
      >
        {showCheckbox && (
          <div
            className="flex items-center min-w-0"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isSelected}
              disabled={isApproving}
              onChange={(e) => {
                // Capture shift state from the underlying click so the
                // hook can drive shift-click range selection.
                const shiftKey = Boolean(
                  (e.nativeEvent as MouseEvent).shiftKey,
                );
                onSelectChange?.(
                  String(mapping.id),
                  e.target.checked,
                  shiftKey,
                );
              }}
              data-testid={`checkbox-${mapping.id}`}
              className={cn(
                "w-4 h-4 rounded border-slate-300 dark:border-slate-600",
                "text-indigo-600 focus:ring-indigo-500 focus:ring-offset-0",
                "cursor-pointer flex-shrink-0",
              )}
            />
          </div>
        )}

        <AttributeReviewSourceLabel
          sourceAttribute={listLine.title}
          requiredStarNode={requiredStarNode}
          onToggle={onToggle}
        />

        <div className="flex items-center min-w-0" onClick={onToggle}>
          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
        </div>

        <div className="min-w-0" onClick={onToggle}>
          <p className="text-sm font-mono text-slate-900 dark:text-slate-100 break-words">
            {listLine.target}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap mt-1">
            <AttributeReviewRowChips chips={listLine.marks} />
          </div>
        </div>

        {showConfidence && (
          <div className="min-w-0 flex flex-wrap items-center gap-2">
            {/* Confidence only matters while a decision is open: settled rows
              (approved or rejected) show just their action button. */}
            {!isRejected && !isPreApproved && (
              <div
                className="flex-1 min-w-0 flex flex-col justify-center"
                onClick={onToggle}
              >
                <div className="flex items-center gap-1.5 flex-wrap">
                  {requiresManualDecisions && (
                    <span
                      className="inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-semibold text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200 flex-shrink-0"
                      title="At least one enum value or value-review decision is still unresolved. Open the row to finish."
                    >
                      Needs decisions
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div
          className="flex items-center justify-end gap-2 min-w-0"
          onClick={(event) => event.stopPropagation()}
        >
          {!isRowStaged &&
            !requiresManualDecisions &&
            canInlineApprove(mapping) &&
            onApproveRowImmediately && (
              <button
                type="button"
                onClick={handleInlineApproveClick}
                disabled={isStaging || isCommittingBucket || isApproving}
                data-testid={`row-inline-approve-${mapping.id}`}
                title="Approve this fully-confident mapping without opening the review modal"
                aria-label="Approve mapping"
                className={cn(
                  "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold flex-shrink-0",
                  "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
                  "dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-950/70",
                  "focus:outline-none focus:ring-1 focus:ring-emerald-500",
                  "disabled:cursor-not-allowed disabled:opacity-60",
                )}
              >
                {isStaging || isCommittingBucket ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
                Approve
              </button>
            )}
          {!isRejected && !isPreApproved && (
            <button
              type="button"
              onClick={() => setRejectFieldConfirmOpen(true)}
              disabled={
                !onRejectField || isApproving || isStaging || isCommittingBucket
              }
              data-testid={`reject-field-${mapping.id}`}
              title="Ignore this attribute"
              aria-label="Ignore this attribute"
              className={cn(
                "inline-flex h-8 w-8 items-center justify-center rounded-md border",
                "border-rose-200 bg-white text-rose-600 hover:bg-rose-50 hover:text-rose-800",
                "dark:border-rose-900 dark:bg-slate-900 dark:text-rose-400 dark:hover:bg-rose-950/40 dark:hover:text-rose-300",
                "focus:outline-none focus:ring-1 focus:ring-rose-500",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {isRejected && onUnreject && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void onUnreject();
              }}
              data-testid={`row-unreject-${mapping.id}`}
              title="Undo and return this attribute to Needs Review"
              className={cn(
                "inline-flex items-center gap-1 rounded border px-2 py-1 text-xs font-medium flex-shrink-0",
                "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800",
                "focus:outline-none focus:ring-1 focus:ring-indigo-500",
              )}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Undo
            </button>
          )}
        </div>
      </div>

      <Dialog.Root
        open={isExpanded}
        onOpenChange={(open) => {
          if (open) return;
          // Hard lock while the bulk approve mutation is in flight: the
          // reviewer must not navigate away mid-save.
          if (isCommittingBucket) return;
          // Bucket has staged work → intercept the close and ask the
          // reviewer to save or discard.
          if (approvedBucketSize > 0) {
            setShowSaveDiscardPrompt(true);
            return;
          }
          onToggle();
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay
            className="fixed inset-0 bg-slate-950/55 backdrop-blur-sm z-40 animate-fade-in"
            onClick={(event) => {
              if (isCommittingBucket || showSaveDiscardPrompt) {
                event.preventDefault();
              }
            }}
          />
          <Dialog.Content
            data-testid={`row-modal-${mapping.id}`}
            onEscapeKeyDown={(event) => {
              if (isCommittingBucket || showSaveDiscardPrompt) {
                event.preventDefault();
              }
            }}
            onPointerDownOutside={(event) => {
              if (isCommittingBucket || showSaveDiscardPrompt) {
                event.preventDefault();
              }
            }}
            className={cn(
              "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50",
              "flex flex-col w-[calc(100vw-2rem)] max-w-6xl max-h-[calc(100vh-3rem)]",
              "rounded-xl bg-white dark:bg-slate-900",
              "border border-slate-200 dark:border-slate-800 shadow-2xl",
              "animate-dialog-fade-in overflow-hidden",
            )}
          >
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
              <div className="min-w-0">
                <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Review attribute mapping
                </p>
                <Dialog.Title className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-base font-semibold text-slate-950 dark:text-slate-50">
                  <span className="break-words [overflow-wrap:anywhere]">
                    {sourceAttributeLabel}
                    {requiredStarNode}
                  </span>
                  <span className="text-slate-400 dark:text-slate-500">
                    &gt;
                  </span>
                  <span className="font-mono text-sm break-words [overflow-wrap:anywhere] text-slate-900 dark:text-slate-100">
                    {listLine.target}
                  </span>
                </Dialog.Title>
              </div>
              <div className="flex items-center gap-2">
                <Dialog.Close asChild>
                  <button
                    data-testid={`row-modal-close-${mapping.id}`}
                    aria-label="Close"
                    className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            <div
              data-testid={`expanded-${mapping.id}`}
              className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-800/30 p-6"
            >
              <div
                className={cn(
                  "grid gap-8",
                  ((mapping.values as unknown[] | null)?.length ?? 0) === 0
                    ? "grid-cols-2"
                    : "grid-cols-1",
                )}
              >
                <div>
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">
                    <Sparkles className="w-4 h-4 text-indigo-500" />
                    Decision Details
                  </h4>

                  <div className="space-y-3 p-4 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        Kind · leaf source
                      </span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {[mapping.kind, mapping.leafSource]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </span>
                    </div>

                    <div className="flex items-start justify-between gap-4 pt-2 border-t border-slate-100 dark:border-slate-800">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        Path (from the engine)
                      </span>
                      <span className="text-sm font-medium font-mono text-right text-slate-900 dark:text-slate-100 break-words [overflow-wrap:anywhere]">
                        {String(mapping.pathPreview ?? "—")}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 p-3 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                    <div className="mb-1 flex items-center justify-between gap-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Marketplace JSON
                      </p>
                      <button
                        type="button"
                        onClick={() => void handleCopyMarketplaceJson()}
                        title="Copy Marketplace JSON"
                        aria-label="Copy Marketplace JSON"
                        className={cn(
                          "inline-flex h-7 w-7 items-center justify-center rounded-md",
                          "text-slate-500 hover:bg-slate-100 hover:text-slate-900",
                          "dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100",
                          "focus:outline-none focus:ring-1 focus:ring-indigo-500",
                        )}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <pre className="max-h-72 overflow-auto rounded-md bg-slate-950 p-3 text-xs leading-relaxed text-slate-100">
                      <code>{marketplaceRawJsonText}</code>
                    </pre>
                  </div>

                  <div className="mt-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-300 mb-1">
                          LLM reasoning
                        </p>
                        <p className="text-sm text-blue-700 dark:text-blue-300 break-words">
                          {llmReasoning ??
                            "No LLM reasoning captured for this mapping."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">
                    <Pencil className="w-4 h-4 text-amber-500" />
                    Review Decision
                  </h4>

                  <LeafDecisionBox
                    mapping={mapping}
                    decision={decision}
                    actions={actions}
                    sessionId={String(sessionId ?? "")}
                  />
                  <PoolSection
                    canTakePool={
                      Boolean(draftSelection?.leaf ?? mapping.leaf) &&
                      (Boolean((mapping.values as unknown[] | null)?.length) ||
                        Number(mapping.sourceEnumValueCount ?? 0) > 0)
                    }
                    pool={shown.pool as Pool | null}
                    values={shown.values as PoolValue[]}
                    fixes={decision.fixes}
                    setFix={decision.setFix}
                    undoFix={decision.undoFix}
                    choosePool={actions.choosePool}
                    refresh={actions.refreshNames}
                    busy={actions.busy}
                  />
                </div>
              </div>
            </div>

            {saveError && (
              <div
                data-testid={`row-modal-save-error-${mapping.id}`}
                className="border-t border-rose-200 bg-rose-50 px-6 py-3 dark:border-rose-800 dark:bg-rose-950/30"
              >
                <p className="text-sm font-medium text-rose-700 dark:text-rose-300">
                  {saveError}
                </p>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-950/40">
              {hasPrev && onNavigatePrev ? (
                <Button
                  data-testid={`row-modal-prev-${mapping.id}`}
                  variant="outline"
                  onClick={onNavigatePrev}
                  disabled={isCommittingBucket}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Previous
                </Button>
              ) : (
                <span />
              )}
              <div className="flex flex-wrap items-center gap-2">
                {approvedBucketSize > 0 && (
                  <span
                    data-testid={`row-modal-bucket-count-${mapping.id}`}
                    className="text-xs font-medium text-emerald-700 dark:text-emerald-300"
                  >
                    {approvedBucketSize} approved · unsaved
                  </span>
                )}
                {isRowStaged ? (
                  <Button
                    data-testid={`row-modal-revert-${mapping.id}`}
                    variant="outline"
                    onClick={handleRevertClick}
                    disabled={isCommittingBucket}
                  >
                    <RotateCcw className="h-4 w-4" />
                    Revert (staged)
                  </Button>
                ) : (
                  <>
                    <Button
                      data-testid={`row-modal-approve-${mapping.id}`}
                      variant="outline"
                      onClick={handleApproveFooterClick}
                      disabled={
                        isStaging ||
                        isSavingRow ||
                        isCommittingBucket ||
                        isApproving
                      }
                    >
                      {isStaging ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Approving…
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4" />
                          Approve
                        </>
                      )}
                    </Button>
                    {onSaveRow && (
                      <Button
                        data-testid={`row-modal-save-${mapping.id}`}
                        onClick={handleSaveClick}
                        disabled={
                          isStaging ||
                          isSavingRow ||
                          isCommittingBucket ||
                          isApproving
                        }
                      >
                        {isSavingRow || isCommittingBucket ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Saving…
                          </>
                        ) : approvedBucketSize > 0 ? (
                          <>
                            <Check className="h-4 w-4" />
                            Save these {approvedBucketSize}
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4" />
                            Save
                          </>
                        )}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
            {showSaveDiscardPrompt && !isCommittingBucket && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/95 px-6 dark:bg-slate-900/95">
                <div className="max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-700 dark:bg-slate-900">
                  <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    Save your approvals?
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                    {approvedBucketSize === 1
                      ? "1 approved mapping is staged but not yet saved."
                      : `${approvedBucketSize} approved mappings are staged but not yet saved.`}
                  </p>
                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    <Button
                      data-testid={`row-modal-cancel-close-${mapping.id}`}
                      variant="ghost"
                      onClick={() => setShowSaveDiscardPrompt(false)}
                    >
                      Keep reviewing
                    </Button>
                    <Button
                      data-testid={`row-modal-discard-${mapping.id}`}
                      variant="outline"
                      onClick={() => {
                        setShowSaveDiscardPrompt(false);
                        onDiscardBucket();
                        onToggle();
                      }}
                    >
                      Discard
                    </Button>
                    <Button
                      data-testid={`row-modal-save-bucket-${mapping.id}`}
                      onClick={async () => {
                        setShowSaveDiscardPrompt(false);
                        await onCommitBucket();
                      }}
                    >
                      <Check className="h-4 w-4" />
                      Save these {approvedBucketSize}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {isCommittingBucket && (
              <div
                data-testid={`row-modal-saving-overlay-${mapping.id}`}
                className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/95 backdrop-blur-sm dark:bg-slate-900/95"
              >
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                <p className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
                  {approvalPipeline
                    ? getApprovalPipelineStatusLabel(approvalPipeline.status)
                    : "Saving all your changes…"}
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {approvalPipeline?.message ??
                    "Submitting the approval request… background tracking will continue after this closes."}
                </p>
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ConfirmationModal
        open={rejectFieldConfirmOpen}
        onOpenChange={(open) => {
          if (!open && !isRejectingField) setRejectFieldConfirmOpen(false);
        }}
        title="Ignore this attribute?"
        eyebrow="Attribute review"
        description={
          <span>
            <span className="font-mono">{sourceAttributeLabel}</span> will be
            marked Ignored, kept visible in the reviewed list, and left out of
            the published schema. You can undo this later to bring it back to
            Needs Review.
          </span>
        }
        variant="danger"
        confirmLabel={isRejectingField ? "Ignoring..." : "Ignore attribute"}
        onConfirm={handleRejectFieldConfirm}
      />
    </div>
  );
};
